export type StructuredDomain = {
  domain: string
  url: string
  isPrimary?: boolean
}

type StructuredDomainCandidate =
  | string
  | {
      domain?: string | null
      url?: string | null
      isPrimary?: boolean | null
    }

const DOMAIN_LABEL_REGEX = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/i

/**
 * Options for the host-canonicalizing helpers.
 *
 * `preserveWww` keeps a leading `www.` on the host instead of stripping
 * it. The default (strip) is correct for IDENTITY (brand dedup keys,
 * the stored `customBrands.domain`) — `www.foo.com` and `foo.com` must
 * collapse to the same canonical brand. But it is WRONG for the FETCH
 * path when an origin only publishes a `www.` DNS record and the apex
 * does not resolve (e.g. Databutton/Vercel-hosted sites). Those callers
 * pass `preserveWww: true` so the resolved-reachable host survives all
 * the way to the fetch. See `resolveReachableHost` in
 * `lib/brand/primitives/probe.ts`.
 */
export type SanitizeHostOptions = { preserveWww?: boolean }

export function sanitizeDomain(
  input: string,
  options?: SanitizeHostOptions
): string | undefined {
  const parsed = parseUrlLike(input)
  if (!parsed) {
    return
  }

  let hostname = parsed.hostname.toLowerCase().replace(/\.+$/, '')
  if (!options?.preserveWww) {
    hostname = hostname.replace(/^www\./, '')
  }

  if (!isValidDomain(hostname)) {
    return
  }
  return hostname
}

export function sanitizeUrlForExternalFetch(
  input: string,
  options?: SanitizeHostOptions
): string | undefined {
  const domain = sanitizeDomain(input, options)
  return domain ? `https://${domain}` : undefined
}

/**
 * Validates the host of a URL-like input but PRESERVES the path and search
 * string. Returns `undefined` when the input cannot be parsed as a host.
 *
 * Use this for inner-page external fetches (e.g. rendered-HTML recovery,
 * inner-page screenshots) where dropping the path produces a redundant
 * homepage fetch — the bug that motivated this helper. Use
 * `sanitizeUrlForExternalFetch` only for the top-level domain canonicalization
 * path (brand-data lookup, homepage capture).
 *
 * Behaviour:
 *  - `https://www.clay.com/customers?utm=foo#bar` -> `https://clay.com/customers?utm=foo`
 *  - `clay.com/customers` -> `https://clay.com/customers`
 *  - leading `www.` is stripped from the host so we match the canonical
 *    domain we use everywhere else
 *  - `#fragment` is dropped (irrelevant for server-side fetches)
 *  - returns `undefined` for invalid hosts or unparseable inputs
 */
export function sanitizeUrlPreservingPath(
  input: string,
  options?: SanitizeHostOptions
): string | undefined {
  const parsed = parseUrlLike(input)
  if (!parsed) {
    return
  }

  let hostname = parsed.hostname.toLowerCase().replace(/\.+$/, '')
  if (!options?.preserveWww) {
    hostname = hostname.replace(/^www\./, '')
  }
  if (!isValidDomain(hostname)) {
    return
  }

  const protocol = parsed.protocol === 'http:' ? 'http:' : 'https:'
  const search = parsed.search || ''
  const path = parsed.pathname || '/'
  return `${protocol}//${hostname}${path}${search}`
}

export function sanitizeStructuredDomains(
  value: Array<StructuredDomainCandidate> | null | undefined
): Array<StructuredDomain> | undefined {
  if (!Array.isArray(value) || value.length === 0) {
    return
  }

  const deduped = new Map<string, StructuredDomain>()

  for (const entry of value) {
    const rawValue =
      typeof entry === 'string' ? entry : entry.url || entry.domain || ''
    const domain = sanitizeDomain(rawValue)
    if (!domain) {
      continue
    }

    const existing = deduped.get(domain)
    const normalized: StructuredDomain = {
      domain,
      url: `https://${domain}`,
      ...(typeof entry === 'object' && entry?.isPrimary
        ? { isPrimary: true }
        : {}),
    }

    if (!existing) {
      deduped.set(domain, normalized)
      continue
    }

    if (!existing.isPrimary && normalized.isPrimary) {
      deduped.set(domain, normalized)
    }
  }

  if (deduped.size === 0) {
    return
  }

  const domains = Array.from(deduped.values())
  const primaryIndex = domains.findIndex((entry) => entry.isPrimary)

  if (primaryIndex === -1) {
    domains[0] = { ...domains[0], isPrimary: true }
    return domains
  }

  if (primaryIndex > 0) {
    const [primary] = domains.splice(primaryIndex, 1)
    domains.unshift(primary)
  }

  return domains
}

function parseUrlLike(input: string): URL | undefined {
  const trimmed = input.trim().replace(/^['"]+|['"]+$/g, '')
  if (!trimmed) {
    return
  }

  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, '')}`

  try {
    return new URL(candidate)
  } catch {}
}

function isValidDomain(hostname: string): boolean {
  if (!hostname || hostname.length > 253) {
    return false
  }
  if (!hostname.includes('.')) {
    return false
  }

  const labels = hostname.split('.')
  return labels.every((label) => DOMAIN_LABEL_REGEX.test(label))
}
